import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { encode } from 'uqr';
import { Check, CircleAlert, Copy, Smartphone } from 'lucide-react';
import { Button, Field, Input } from '@/components/ui';

/**
 * Binding an authenticator app: the QR code, the key for typing in by hand, and
 * the field for the first code.
 *
 * Used both at sign-in (for a role that must have MFA and has none) and from
 * account settings. The caller decides what confirming does; this only
 * collects the code.
 */

export interface EnrollmentMaterial {
  readonly secret: string;
  readonly otpauthUri: string;
}

/**
 * The QR code, drawn as SVG rather than inserted as markup.
 *
 * Always dark on white, whatever the theme: phone cameras are tuned for printed
 * codes, and an inverted one fails to scan on a fair number of them.
 */
function QrCode({ value, label }: { readonly value: string; readonly label: string }): ReactNode {
  const matrix = useMemo(() => encode(value, { ecc: 'M' }), [value]);
  const quiet = 3;
  const size = matrix.size + quiet * 2;

  // One path for every dark module rather than one element each: a version 6
  // code is ~1600 modules, and 1600 DOM nodes for a picture is waste.
  const path = useMemo(() => {
    const parts: string[] = [];
    matrix.data.forEach((row, y) =>
      row.forEach((dark, x) => {
        if (dark) parts.push(`M${x + quiet} ${y + quiet}h1v1h-1z`);
      }),
    );
    return parts.join('');
  }, [matrix]);

  return (
    <svg
      className="qr"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
    >
      <rect width={size} height={size} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}

/** The key, grouped in fours: 32 characters in one run is where people lose
 *  their place and type the same group twice. */
function groupKey(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

export function AuthenticatorSetup({
  material,
  busy,
  error,
  submitLabel,
  onSubmit,
}: {
  readonly material: EnrollmentMaterial;
  readonly busy: boolean;
  readonly error: string | null;
  readonly submitLabel: string;
  readonly onSubmit: (code: string) => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(material.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the key is on screen to type instead.
    }
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const cleaned = code.replace(/\s/g, '');
    if (cleaned.length === 6 && !busy) onSubmit(cleaned);
  };

  return (
    <form className="stack" onSubmit={submit}>
      <ol className="enroll-steps">
        <li>
          <span className="enroll-step-title">
            <Smartphone size={14} aria-hidden="true" />
            Откройте приложение-аутентификатор
          </span>
          <span className="field-hint">Google Authenticator, 1Password, Bitwarden, Aegis — любое с TOTP</span>
        </li>
        <li>
          <span className="enroll-step-title">Отсканируйте код</span>
          <div className="enroll-qr">
            <QrCode value={material.otpauthUri} label="QR-код для приложения-аутентификатора" />
          </div>
          <div className="enroll-key">
            <span className="field-hint">Или введите ключ вручную</span>
            <div className="row-tight">
              <code className="mono enroll-key-value">{groupKey(material.secret)}</code>
              <Button
                size="sm"
                variant="ghost"
                icon={copied ? Check : Copy}
                onClick={() => void copy()}
                aria-label="Скопировать ключ"
              >
                {copied ? 'Скопирован' : 'Копировать'}
              </Button>
            </div>
          </div>
        </li>
        <li>
          <span className="enroll-step-title">Введите код из приложения</span>
          <Field label="Код подтверждения" htmlFor="enroll-code" {...(error ? { error } : {})}>
            <Input
              id="enroll-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/[^\d\s]/g, ''))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              placeholder="000000"
              invalid={error !== null}
              autoFocus
            />
          </Field>
        </li>
      </ol>

      {error === null ? null : (
        <p className="field-hint row-tight">
          <CircleAlert size={13} aria-hidden="true" />
          Код меняется каждые 30 секунд. Если время на телефоне сбито, коды не совпадут.
        </p>
      )}

      <Button
        type="submit"
        variant="primary"
        block
        busy={busy}
        disabled={code.replace(/\s/g, '').length !== 6}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
